import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';
import { formatMoney } from '../shared/formatting';

type Locale = 'hr' | 'en';
type Category = 'long_asset' | 'short_liability' | 'neutral';

type ExtraRow = {
    id: number;
    label: string;
    category: Category;
    eur_value: number;
    notes: string | null;
    is_active: 0 | 1 | boolean;
    updated_by_name: string | null;
    updated_at: string;
};

type Suggestion = { label: string; category: Category };

type ListResponse = {
    rows: ExtraRow[];
    suggestions: Suggestion[];
};

const CATEGORY_META: Record<Category, { hr: string; en: string; color: string; bg: string }> = {
    long_asset: { hr: 'LONG (imovina)', en: 'LONG (asset)', color: '#1f7a36', bg: 'rgba(31,122,54,0.10)' },
    short_liability: { hr: 'SHORT (obveza)', en: 'SHORT (liability)', color: '#b14040', bg: 'rgba(177,64,64,0.10)' },
    neutral: { hr: 'Neutral (info)', en: 'Neutral (info)', color: 'var(--ad-acc-3)', bg: 'rgba(var(--ad-acc-rgb),0.10)' },
};

export function AdminGoldGuaranteesSection({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<ListResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState<ExtraRow | null>(null);
    const [adding, setAdding] = useState<Partial<ExtraRow> | null>(null);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            setData(await api<ListResponse>('/admin/gold-guarantees', token));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { void load(); }, [load]);

    async function save(row: { id?: number; label: string; category: Category; eur_value: number; notes: string | null; is_active: boolean }) {
        try {
            if (row.id) {
                await mutate(`/admin/gold-guarantees/${row.id}`, token, 'PATCH', row);
            } else {
                await mutate('/admin/gold-guarantees', token, 'POST', row);
            }
            setEditing(null); setAdding(null);
            await load();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'API');
        }
    }

    async function deleteRow(id: number) {
        if (!confirm(t('Obrisati ovu stavku?', 'Delete this item?'))) return;
        try {
            await mutate(`/admin/gold-guarantees/${id}`, token, 'DELETE');
            await load();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'API');
        }
    }

    if (loading && !data) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    if (error) return <p className="error">{error}</p>;
    if (!data) return null;

    const totalLong = data.rows.filter((r) => r.category === 'long_asset' && r.is_active).reduce((s, r) => s + Number(r.eur_value), 0);
    const totalShort = data.rows.filter((r) => r.category === 'short_liability' && r.is_active).reduce((s, r) => s + Number(r.eur_value), 0);

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🏦</div>
                <div className="bi-module-hero-text">
                    <h1>128 · {t('Garancije i manual pozicije', 'Guarantees & manual positions')}</h1>
                    <p className="muted">{t(
                        'Ručno upisani brojevi koji ulaze u bilancu Net Gold Position (m.40.5). Argor/StoneX garancije, hedge cash, intercompany pozajmice, Bugarska AR, cash po računima — sve dok ne dođu prave integracije.',
                        'Manual numbers feeding Net Gold Position balance (m.40.5). Argor/StoneX guarantees, hedge cash, intercompany loans, Bulgaria AR, cash by account — until real integrations land.',
                    )}</p>
                </div>
            </div>

            {/* Quick add suggestions */}
            {data.suggestions.length > 0 ? (
                <div style={{
                    background: 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)',
                    border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 14,
                }}>
                    <strong style={{ color: 'var(--ad-ink)', fontSize: '0.86rem' }}>💡 {t('Predloženo (klik za dodati)', 'Suggested (click to add)')}</strong>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                        {data.suggestions.map((s) => (
                            <button
                                key={s.label}
                                type="button"
                                className="secondary compact"
                                onClick={() => setAdding({ label: s.label, category: s.category, eur_value: 0, notes: null, is_active: true })}
                                style={{ fontSize: '0.78rem' }}
                            >
                                + {s.label}
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}

            {/* Totals */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 14 }}>
                <div style={{ background: CATEGORY_META.long_asset.bg, borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: '0.7rem', color: CATEGORY_META.long_asset.color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>LONG</div>
                    <strong style={{ fontSize: '1.1rem' }}>{formatMoney(totalLong, locale)}</strong>
                </div>
                <div style={{ background: CATEGORY_META.short_liability.bg, borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: '0.7rem', color: CATEGORY_META.short_liability.color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>SHORT</div>
                    <strong style={{ fontSize: '1.1rem' }}>{formatMoney(totalShort, locale)}</strong>
                </div>
                <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{t('Net doprinos', 'Net contribution')}</div>
                    <strong style={{ fontSize: '1.1rem', color: totalLong - totalShort >= 0 ? '#1f7a36' : '#b14040' }}>{formatMoney(totalLong - totalShort, locale)}</strong>
                </div>
            </div>

            <button type="button" className="compact" onClick={() => setAdding({ label: '', category: 'long_asset', eur_value: 0, notes: null, is_active: true })} style={{ marginBottom: 12 }}>
                + {t('Dodaj stavku', 'Add item')}
            </button>

            <div className="table-wrap">
                <table className="admin-user-table" style={{ fontSize: '0.86rem' }}>
                    <thead>
                        <tr>
                            <th>{t('Stavka', 'Item')}</th>
                            <th>{t('Kategorija', 'Category')}</th>
                            <th style={{ textAlign: 'right' }}>EUR</th>
                            <th>{t('Napomena', 'Notes')}</th>
                            <th>{t('Aktivno', 'Active')}</th>
                            <th>{t('Ažurirano', 'Updated')}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.length === 0 ? (
                            <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: '1.5rem', fontStyle: 'italic' }}>{t('Nema stavki. Klikni suggestion gore ili "Dodaj stavku".', 'No items. Click a suggestion above or "Add item".')}</td></tr>
                        ) : data.rows.map((r) => {
                            const cat = CATEGORY_META[r.category];
                            return (
                                <tr key={r.id} style={!r.is_active ? { opacity: 0.5 } : undefined}>
                                    <td><strong>{r.label}</strong></td>
                                    <td>
                                        <span style={{ background: cat.bg, color: cat.color, padding: '2px 8px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700 }}>
                                            {locale === 'en' ? cat.en : cat.hr}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: 'right', fontWeight: 700, color: cat.color }}>{formatMoney(r.eur_value, locale)}</td>
                                    <td className="muted" style={{ fontSize: '0.78rem' }}>{r.notes ?? '—'}</td>
                                    <td>{r.is_active ? '✓' : <span className="muted">—</span>}</td>
                                    <td className="muted" style={{ fontSize: '0.72rem' }}>{r.updated_by_name ?? '—'} · {r.updated_at?.replace('T', ' ').slice(0, 16)}</td>
                                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                                        <button type="button" className="secondary compact" onClick={() => setEditing(r)} style={{ fontSize: '0.72rem', padding: '2px 8px', marginRight: 3 }}>✏</button>
                                        <button type="button" className="secondary compact" onClick={() => void deleteRow(r.id)} style={{ fontSize: '0.72rem', padding: '2px 8px', color: '#b14040' }}>🗑</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {(editing || adding) ? (
                <EditModal
                    locale={locale}
                    initial={editing ?? (adding as ExtraRow)}
                    onClose={() => { setEditing(null); setAdding(null); }}
                    onSave={(d) => void save({ ...d, id: editing?.id })}
                />
            ) : null}

            <p className="muted" style={{ fontSize: '0.74rem', marginTop: 14, fontStyle: 'italic' }}>
                💡 {t(
                    'Sve stavke automatski se uračunavaju u bilancu u m.40.5 Net Position (vidljivo samo superadmin-ovima).',
                    'All items automatically feed the balance in m.40.5 Net Position (visible only to superadmins).',
                )}
            </p>
        </section>
    );
}

function EditModal({ locale, initial, onClose, onSave }: {
    locale: Locale;
    initial: Partial<ExtraRow>;
    onClose: () => void;
    onSave: (d: { label: string; category: Category; eur_value: number; notes: string | null; is_active: boolean }) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [label, setLabel] = useState(initial.label ?? '');
    const [category, setCategory] = useState<Category>(initial.category ?? 'long_asset');
    const [eurValue, setEurValue] = useState(String(initial.eur_value ?? ''));
    const [notes, setNotes] = useState(initial.notes ?? '');
    const [isActive, setIsActive] = useState(initial.is_active !== false);

    function submit() {
        const v = parseFloat(eurValue);
        if (!label.trim() || !Number.isFinite(v)) {
            alert(t('Label i iznos su obavezni.', 'Label and amount required.'));
            return;
        }
        onSave({ label: label.trim(), category, eur_value: v, notes: (notes ?? '').trim() || null, is_active: isActive });
    }

    return (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(540px, 96vw)', background: '#fff', borderRadius: 12, padding: '18px 22px', boxShadow: '0 20px 60px rgba(107,79,28,0.28)' }}>
                <h3 style={{ margin: '0 0 12px', color: 'var(--ad-ink)' }}>{initial.id ? t('Uredi stavku', 'Edit item') : t('Nova stavka', 'New item')}</h3>
                <label className="filter-label" style={{ display: 'block', marginBottom: 8 }}>
                    {t('Label', 'Label')}
                    <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} style={{ display: 'block', width: '100%', marginTop: 4 }} placeholder="Argor — garancija" />
                </label>
                <label className="filter-label" style={{ display: 'block', marginBottom: 8 }}>
                    {t('Kategorija', 'Category')}
                    <select value={category} onChange={(e) => setCategory(e.target.value as Category)} style={{ display: 'block', width: '100%', marginTop: 4 }}>
                        <option value="long_asset">{t('LONG — imovina (ide u Net +)', 'LONG — asset (Net +)')}</option>
                        <option value="short_liability">{t('SHORT — obveza (ide u Net −)', 'SHORT — liability (Net −)')}</option>
                        <option value="neutral">{t('Neutral — info-only', 'Neutral — info-only')}</option>
                    </select>
                </label>
                <label className="filter-label" style={{ display: 'block', marginBottom: 8 }}>
                    {t('Iznos EUR', 'Amount EUR')}
                    <input type="number" step="0.01" value={eurValue} onChange={(e) => setEurValue(e.target.value)} style={{ display: 'block', width: '100%', marginTop: 4 }} />
                </label>
                <label className="filter-label" style={{ display: 'block', marginBottom: 8 }}>
                    {t('Napomena', 'Notes')}
                    <textarea value={notes ?? ''} onChange={(e) => setNotes(e.target.value)} style={{ display: 'block', width: '100%', marginTop: 4, minHeight: 60 }} />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: '0.86rem' }}>
                    <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                    {t('Aktivno (uračunava se)', 'Active (counted)')}
                </label>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button type="button" onClick={onClose} className="secondary compact">{t('Odustani', 'Cancel')}</button>
                    <button type="button" onClick={submit}>{t('Spremi', 'Save')}</button>
                </div>
            </div>
        </div>
    );
}
